import { cacheService } from './cache.service.js'
import { apiService } from './api.service.js'
import { AUTH_CONSTANTS } from '../constants/auth.constants.js'

/**
 * Serviço de cache para transações com cache por período
 * Implementa padrão cache-first com TTL de 10 minutos,
 * deduplicação de requisições simultâneas, processamento de
 * respostas enriquecidas do backend e fallback offline.
 *
 * Chave de cache: transacoes_{mes}_{ano} (ex: transacoes_01_2025)
 * Eventos disparados: transacoes-cache-updated, saldo-mensal-cache-updated
 */
export class TransacaoCacheService {
  constructor() {
    this.cacheKeyPrefix = 'transacoes_'
    this.endpoint = 'transacoes'
    /** Map de Promises em andamento para deduplicação por período */
    this.loadingPromises = new Map()
    this.periodosCarregadosKey = 'periodos_carregados'
  }

  /**
   * Lista transações do período atual (mês corrente)
   * Utiliza cache-first com TTL de 10 minutos via cacheService.isValid()
   * @returns {Promise<Array>} Lista de transações do período inicial
   */
  async listar() {
    const periodos = this._getPeriodosIniciais()
    const todasTransacoes = []

    for (const periodo of periodos) {
      const transacoes = await this.listarPorPeriodo(periodo.mes, periodo.ano)
      todasTransacoes.push(...transacoes)
    }

    return todasTransacoes
  }

  /**
   * Busca transações por filtro de período (formato YYYY-MM)
   * @param {Object} filtros - Filtros de busca com campo 'periodo'
   * @returns {Promise<Array>} Transações filtradas
   */
  async buscarPorFiltro(filtros = {}) {
    if (filtros.periodo && filtros.periodo !== 'todos') {
      const [ano, mes] = filtros.periodo.split('-')
      return this.listarPorPeriodo(mes, ano)
    }
    return this.listar()
  }

  /**
   * Lista transações de um período específico com cache-first
   * Usa cacheService.isValid() para verificação centralizada de TTL (10 min)
   * Implementa deduplicação via loadingPromises Map
   * Implementa fallback offline retornando cache expirado quando sem conexão
   *
   * @param {string} mes - Mês com zero à esquerda (ex: '01')
   * @param {string} ano - Ano com 4 dígitos (ex: '2025')
   * @returns {Promise<Array>} Transações do período
   */
  async listarPorPeriodo(mes, ano) {
    const cacheKey = `${this.cacheKeyPrefix}${mes}_${ano}`
    const periodoKey = `${mes}_${ano}`

    // Cache-first: verifica validade com TTL padrão de 10 minutos
    if (cacheService.isValid(cacheKey)) {
      return cacheService.get(cacheKey) || []
    }

    // Fallback offline: retorna cache expirado quando sem conexão
    if (!navigator.onLine) {
      const cacheExpirado = cacheService.get(cacheKey)
      return cacheExpirado || []
    }

    // Deduplicação: reutiliza Promise em andamento para o mesmo período
    if (this.loadingPromises.has(periodoKey)) {
      return this.loadingPromises.get(periodoKey)
    }

    // Cache miss: busca do backend
    const promise = this._loadPeriodoFromBackend(mes, ano, cacheKey, periodoKey)
    this.loadingPromises.set(periodoKey, promise)

    try {
      const result = await promise
      return result
    } finally {
      this.loadingPromises.delete(periodoKey)
    }
  }

  /**
   * Carrega transações de um período do backend via GET.
   * Backend retorna: { success: true, data: [...] }.
   * Em caso de timeout de autenticação (AUTH_TIMEOUT), retorna dados do cache local
   * ou array vazio — sem propagar exceção e sem executar requisição à API.
   *
   * @param {string} mes - Mês do período
   * @param {string} ano - Ano do período
   * @param {string} cacheKey - Chave de cache para armazenamento
   * @param {string} periodoKey - Chave do período para controle de carregados
   * @returns {Promise<Array>} Transações do período
   * @private
   */
  async _loadPeriodoFromBackend (mes, ano, cacheKey, periodoKey) {
    try {
      const response = await apiService.get(`${this.endpoint}?mes=${mes}&ano=${ano}`)
      const transacoes = response.data?.data || response.data || response

      cacheService.set(cacheKey, transacoes)
      this._addPeriodoCarregado(periodoKey)
      return transacoes || []
    } catch (error) {
      // Em timeout de auth, retorna cache local ou array vazio (Req 5.2)
      if (error.code === AUTH_CONSTANTS.AUTH_TIMEOUT_ERROR) {
        const cacheLocal = cacheService.get(cacheKey)
        return cacheLocal || []
      }
      throw error
    }
  }

  /**
   * Retorna período inicial (apenas mês atual)
   * @returns {Array<{mes: string, ano: string}>} Períodos iniciais
   * @private
   */
  _getPeriodosIniciais() {
    const hoje = new Date()
    const anoAtual = hoje.getFullYear()
    const mesAtual = hoje.getMonth() // 0-11

    const periodos = [{
      mes: String(mesAtual + 1).padStart(2, '0'),
      ano: String(anoAtual)
    }]

    return periodos
  }

  /**
   * Cria uma nova transação no backend e atualiza o cache local
   * Processa resposta enriquecida: { success: true, data: { entidade, derivados, avisos } }
   * Atualiza caches derivados (saldoMensal, orçamentos) via _processarDerivados
   * Dispara evento transacoes-cache-updated com período afetado
   *
   * @param {Object} dados - Dados da transação a ser criada
   * @returns {Promise<Object>} Transação criada pelo backend
   */
  async criar(dados) {
    const response = await apiService.post(this.endpoint, dados)
    const responseData = response.data || response

    // Extrai do envelope enriquecido { entidade, derivados, avisos }
    const entidade = responseData.entidade || responseData
    const derivados = responseData.derivados || {}

    // Adiciona ao cache do período correspondente
    if (entidade.data) {
      const { mes, ano } = this._extrairPeriodoDaData(entidade.data)
      if (mes && ano) {
        const cacheKey = `${this.cacheKeyPrefix}${mes}_${ano}`

        // Se cache existe, adiciona a entidade
        if (cacheService.isValid(cacheKey)) {
          cacheService.addToArray(cacheKey, entidade)
        } else {
          // Se cache não existe, busca período do backend para popular cache
          await this.listarPorPeriodo(mes, ano)
        }

        // Processa derivados (saldoMensal, orçamentos)
        this._processarDerivados(derivados)

        // Dispara evento com período afetado
        window.dispatchEvent(new CustomEvent('transacoes-cache-updated', {
          detail: { mes, ano }
        }))
      }
    }

    return entidade
  }

  /**
   * Atualiza uma transação existente no backend e sincroniza o cache local
   * Processa resposta enriquecida e dispara evento com período afetado
   *
   * @param {string} id - ID da transação a ser atualizada
   * @param {Object} dados - Dados atualizados da transação
   * @returns {Promise<Object>} Transação atualizada pelo backend
   */
  async atualizar(id, dados) {
    const response = await apiService.put(`${this.endpoint}/${id}`, dados)
    const responseData = response.data || response

    // Extrai do envelope enriquecido { entidade, derivados, avisos }
    const entidade = responseData.entidade || responseData
    const derivados = responseData.derivados || {}

    // Atualiza em todos os caches de período
    this._updateInAllCaches(id, entidade)

    // Processa derivados (saldoMensal, orçamentos)
    this._processarDerivados(derivados)

    // Extrai período da data da transação atualizada
    const detail = this._extrairPeriodoDaTransacao(entidade)
    window.dispatchEvent(new CustomEvent('transacoes-cache-updated', {
      detail
    }))

    return entidade
  }

  /**
   * Exclui uma transação do backend e remove do cache local
   * Busca o período da transação no cache antes de removê-la
   * Processa resposta enriquecida e dispara evento com período afetado
   *
   * @param {string} id - ID da transação a ser excluída
   * @param {Object} [periodo] - Período opcional {mes, ano} para evitar busca no cache
   * @returns {Promise<boolean>} True se excluída com sucesso
   */
  async excluir(id, periodo) {
    // Usa período informado ou busca no cache antes de remover
    const detail = periodo || this._buscarPeriodoDaTransacaoNoCache(id)

    const response = await apiService.delete(`${this.endpoint}/${id}`)
    const responseData = response.data || response

    // Extrai derivados da resposta enriquecida
    const derivados = responseData.derivados || {}

    // Remove de todos os caches
    this._removeFromAllCaches(id)

    // Processa derivados (saldoMensal, orçamentos)
    this._processarDerivados(derivados)

    // Dispara evento com período afetado
    window.dispatchEvent(new CustomEvent('transacoes-cache-updated', {
      detail
    }))

    return true
  }

  /**
   * Força recarregamento do cache de transações
   * Limpa todos os caches de transações do usuário e recarrega do backend
   * @returns {Promise<Array>} Transações recarregadas
   */
  async recarregar() {
    // Limpa todos os caches de transações do usuário atual
    if (cacheService.currentUserUid) {
      const userPrefix = `equilibra_cache_${cacheService.currentUserUid}_${this.cacheKeyPrefix}`
      Object.keys(localStorage)
        .filter(key => key.startsWith(userPrefix))
        .forEach(key => localStorage.removeItem(key))
    }

    // Limpa períodos carregados
    cacheService.remove(this.periodosCarregadosKey)

    return this.listar()
  }

  /**
   * Processa entidades derivadas da resposta enriquecida do backend
   * Atualiza caches de saldo mensal, orçamentos e contas conforme dados recebidos
   * Dispara eventos de cache-updated para que o dashboard e demais componentes
   * reflitam as mudanças sem requisições adicionais ao backend.
   *
   * @param {Object} derivados - Objeto com entidades derivadas do backend
   * @param {Object} [derivados.saldoMensal] - Saldo mensal recalculado (único período)
   * @param {Array} [derivados.saldosMensais] - Saldos mensais de múltiplos períodos
   * @param {Array} [derivados.orcamentos] - Orçamentos atualizados
   * @param {Array} [derivados.contas] - Lista completa de contas ativas com saldos atualizados
   * @private
   */
  _processarDerivados(derivados) {
    if (!derivados || Object.keys(derivados).length === 0) return

    // Atualiza saldo mensal único se presente
    if (derivados.saldoMensal) {
      const { mes, ano } = derivados.saldoMensal
      const mesStr = String(mes).padStart(2, '0')
      const anoStr = String(ano)
      cacheService.set(`saldo_mensal_${mesStr}_${anoStr}`, derivados.saldoMensal)
      window.dispatchEvent(new CustomEvent('saldo-mensal-cache-updated', {
        detail: { mes: mesStr, ano: anoStr }
      }))
    }

    // Atualiza múltiplos saldos mensais se presentes (ex: mudança de mês)
    if (derivados.saldosMensais && Array.isArray(derivados.saldosMensais)) {
      derivados.saldosMensais.forEach(saldo => {
        const mesStr = String(saldo.mes).padStart(2, '0')
        const anoStr = String(saldo.ano)
        cacheService.set(`saldo_mensal_${mesStr}_${anoStr}`, saldo)
        window.dispatchEvent(new CustomEvent('saldo-mensal-cache-updated', {
          detail: { mes: mesStr, ano: anoStr }
        }))
      })
    }

    // Atualiza orçamentos se presentes (mescla com dados existentes no cache
    // para preservar campos enriquecidos como categoria.nome e categoria.emoji)
    if (derivados.orcamentos && Array.isArray(derivados.orcamentos) && derivados.orcamentos.length > 0) {
      derivados.orcamentos.forEach(orc => {
        const [ano, mes] = orc.periodo.split('-')
        const cacheKey = `orcamentos_${mes}_${ano}`
        // Busca item existente no cache para mesclar (preserva categoria)
        const cached = cacheService.get(cacheKey) || []
        const existente = cached.find(item => item && item.id === orc.id)
        const mesclado = existente ? { ...existente, ...orc } : orc
        cacheService.updateInArray(cacheKey, orc.id, mesclado)
      })
      window.dispatchEvent(new CustomEvent('orcamentos-cache-updated'))
    }

    // Atualiza cache de contas se presente nos derivados
    if (derivados.contas) {
      cacheService.set('contas', derivados.contas)
      window.dispatchEvent(new CustomEvent('contas-cache-updated'))
    }
  }

  /**
   * Atualiza transação em todos os caches de período do usuário
   * @param {string} id - ID da transação
   * @param {Object} transacao - Transação atualizada
   * @private
   */
  _updateInAllCaches(id, transacao) {
    if (!cacheService.currentUserUid) return

    const userPrefix = `equilibra_cache_${cacheService.currentUserUid}_${this.cacheKeyPrefix}`
    Object.keys(localStorage)
      .filter(key => key.startsWith(userPrefix))
      .forEach(key => {
        const cacheKey = key.replace(`equilibra_cache_${cacheService.currentUserUid}_`, '')
        cacheService.updateInArray(cacheKey, id, transacao)
      })
  }

  /**
   * Remove transação de todos os caches de período do usuário
   * @param {string} id - ID da transação
   * @private
   */
  _removeFromAllCaches(id) {
    if (!cacheService.currentUserUid) return

    const userPrefix = `equilibra_cache_${cacheService.currentUserUid}_${this.cacheKeyPrefix}`
    Object.keys(localStorage)
      .filter(key => key.startsWith(userPrefix))
      .forEach(key => {
        const cacheKey = key.replace(`equilibra_cache_${cacheService.currentUserUid}_`, '')
        cacheService.removeFromArray(cacheKey, id)
      })
  }

  /**
   * Extrai mês e ano de uma data no formato DD/MM/AAAA ou YYYY-MM-DD
   * Suporta ambos os formatos para compatibilidade com backend
   *
   * @param {string} data - Data no formato DD/MM/AAAA ou YYYY-MM-DD
   * @returns {{mes: string, ano: string}|{mes: null, ano: null}} Período extraído
   * @private
   */
  _extrairPeriodoDaData(data) {
    if (!data) return { mes: null, ano: null }

    // Formato YYYY-MM-DD (backend)
    if (data.includes('-')) {
      const partes = data.split('-')
      if (partes.length >= 2) {
        return { mes: partes[1], ano: partes[0] }
      }
    }

    // Formato DD/MM/AAAA (legado)
    if (data.includes('/')) {
      const partes = data.split('/')
      if (partes.length === 3) {
        return { mes: partes[1], ano: partes[2] }
      }
    }

    return { mes: null, ano: null }
  }

  /**
   * Extrai mês e ano da data de uma transação
   * Retorna objeto com mes e ano ou null se data inválida
   *
   * @param {Object} transacao - Transação com campo 'data'
   * @returns {{mes: string, ano: string}|null} Período extraído ou null
   * @private
   */
  _extrairPeriodoDaTransacao(transacao) {
    if (!transacao || !transacao.data) return null

    const { mes, ano } = this._extrairPeriodoDaData(transacao.data)
    if (!mes || !ano) return null

    return { mes, ano }
  }

  /**
   * Busca o período de uma transação no cache local antes de removê-la
   * Percorre todos os caches de transações do usuário atual procurando
   * a transação pelo ID para extrair seu período (mes/ano)
   *
   * @param {string} id - ID da transação a ser buscada
   * @returns {{mes: string, ano: string}|null} Período da transação ou null
   * @private
   */
  _buscarPeriodoDaTransacaoNoCache(id) {
    if (!cacheService.currentUserUid) return null

    const userPrefix = `equilibra_cache_${cacheService.currentUserUid}_${this.cacheKeyPrefix}`
    const keys = Object.keys(localStorage).filter(key => key.startsWith(userPrefix))

    for (const key of keys) {
      const cacheKey = key.replace(`equilibra_cache_${cacheService.currentUserUid}_`, '')
      const transacoes = cacheService.get(cacheKey)
      if (Array.isArray(transacoes)) {
        const transacao = transacoes.find(t => t.id === id)
        if (transacao) {
          return this._extrairPeriodoDaTransacao(transacao)
        }
      }
    }

    return null
  }

  /**
   * Verifica se período já foi carregado na sessão
   * @param {string} periodoKey - Chave do período (ex: '05_2025')
   * @returns {boolean} True se período já foi carregado
   * @private
   */
  _isPeriodoCarregado(periodoKey) {
    const periodosCarregados = this._getPeriodosCarregados()
    return periodosCarregados.includes(periodoKey)
  }

  /**
   * Obtém lista de períodos já carregados do cache
   * @returns {Array<string>} Lista de períodos carregados
   * @private
   */
  _getPeriodosCarregados() {
    const cached = cacheService.get(this.periodosCarregadosKey)
    return cached || []
  }

  /**
   * Adiciona período à lista de períodos carregados
   * Evita duplicatas na lista
   * @param {string} periodoKey - Chave do período (ex: '05_2025')
   * @private
   */
  _addPeriodoCarregado(periodoKey) {
    const periodos = this._getPeriodosCarregados()
    if (!periodos.includes(periodoKey)) {
      periodos.push(periodoKey)
      cacheService.set(this.periodosCarregadosKey, periodos)
    }
  }
}

/** Instância singleton do serviço de cache de transações */
export const transacaoCacheService = new TransacaoCacheService()
